import { createManifest } from 'featuredrop'

/**
 * FeatureDrop manifest — the changelog entries surfaced by the "What's New"
 * dropdown, the /changelog page, and the "New" dots in the nav.
 *
 * Placeholders:
 *   {fleet}  — replaced with the selected fleet slug in `cta.url` and in
 *              `description` text (see FeatureDrop.Changelog.substituteFleet).
 *
 * `sidebarKey` — set this to put a "New" dot next to a nav item until the
 * feature is dismissed. Recognised keys (kept in sync with
 * `Html.Layout.sidebarKeyFor`):
 *
 *   "vessels"              — Vessels
 *   "geofences"            — Geofences
 *   "discovery"            — Nebo Discovery > Settings
 *   "places"               — Nebo Discovery > Places
 *   "routes"               — Nebo Discovery > Routes
 *   "fleet-usage-monthly"  — Reports › Fleet Usage (Monthly)
 *   "fleet-usage-range"    — Reports › Fleet Usage (Date Range)
 *   "utilisation-monthly"  — Reports › Utilisation (Monthly)
 *   "utilisation-weekly"   — Reports › Utilisation (Weekly)
 *   "on-time-report"       — Reports › On Time Report
 *   "fleet-config"         — Settings › Fleet Configuration
 *   "fleet-users"          — Settings › Users
 *   "vessel-logs"          — "View Vessel Logs" button on the boat detail panel
 *
 * A dot on a Reports/Settings child also lights up its parent dropdown toggle.
 * Nav keys live in `Html.Layout.sidebarKeyFor`; "vessel-logs" is checked
 * directly in `Pages.Fleet_.boatDetail`.
 *
 * `trigger` — an entry with a `trigger` is hidden from everywhere (dropdown,
 * /changelog, nav dots) until the trigger fires, then shows as an announcement
 * modal (FeatureDrop.Announcement) as well as in the dropdown. Supported:
 *
 *   { type: 'page', match: '<glob>' }      — fires when location.pathname matches
 *       ('*' → '.*', anchored; or prefix match). Slugs are dynamic, so use
 *       wildcards: '*'/'*routes*'. The modal only renders on signed-in fleet
 *       pages, so a broad '*' is fine.
 *   { type: 'milestone', event: 'app_entered' }
 *       — fires once the user has a confirmed-valid session (Shared.enteredApp,
 *       sent from SignIn and the first successful authed request). Use this for
 *       "show on first load / after login".
 *
 * `audience` — restricts an entry to matching fleets/users. `plan`/`role`/`region`
 * aren't wired up (the UserContext is always empty) — setting one hides the
 * entry from everyone. Use these `custom` keys instead, each a JSON array of
 * strings checked in Elm against the selected fleet
 * (FeatureDrop.Changelog.matchesAudience):
 *
 *   `fleetType` — the fleet's type. Valid values: 'tourism', 'workboats',
 *       'rescue', 'commuter', 'vehicles' (Data.Fleet.FleetType).
 *   `fleetRole` — the user's role in the fleet. Valid values: 'administrator',
 *       'user' (Data.User.Role). Nebo admins count as 'administrator'.
 *
 * No `audience`, no `custom`, or a missing key all mean "everyone".
 *
 *   { custom: { fleetType: ['tourism', 'rescue'] } }
 *       — only shown to Tourism and Rescue fleets.
 *   { custom: { fleetRole: ['administrator'] } }
 *       — only shown to fleet administrators, e.g. when the CTA links to a
 *       Settings page users can't open.
 */
export const features = createManifest([
    {
        id: 'new-features',
        label: 'New Features',
        description:
            "We've added some great new features to Nebo Fleet. You'll find details at the top right of the screen (look for the bell icon). Menu items with new features are also highlighted with a green dot. Please send feedback to [fleet.support@nebo.global](mailto:fleet.support@nebo.global)",
        releasedAt: '2026-09-24',
        showNewUntil: '2026-10-02',
        trigger: { type: 'milestone', event: 'app_entered' }
    },
    {
        id: 'after-hours-alert',
        label: 'After Hours Alert',
        description:
            "Configure any or all of your boats to trigger an alert if it moves after hours.  You can specify your operating hours, or choose \"Daylight Hours\" and Nebo Fleet will alert you on movement between sunset and sunrise (automatically adjusted each day).",
        releasedAt: '2026-09-24',
        showNewUntil: '2026-10-24',
        sidebarKey: "after-hours",
    },
    {
        id: 'nebo-discovery',
        label: 'Nebo Discovery',
        description:
            "Designed for rental, charter and member-based fleets, Nebo Discovery allows guests or members to scan a QR code with their own device, and to view a map showing their location, points of interest, recommended routes, restricted areas and more. Go to the new Nebo Discovery menu to add Places and Routes, and to set up what your guests can see.",
        releasedAt: '2026-09-24',
        showNewUntil: '2026-10-24',
        sidebarKey: "discovery",
        audience: { custom: { fleetType: ['tourism'] } }
    },
    {
        id: 'nebo-replay',
        label: 'Nebo Replay',
        description:
            "Nebo Replay allows you or your rental/charter guests to create an animated movie of their day on the water. It's great for sharing to socials, generating more reach for your business. Tap a boat, then View Vessel Logs, choose a time period and then Save > Nebo Replay (at the top right).",
        releasedAt: '2026-09-24',
        showNewUntil: '2026-10-24',
        sidebarKey: "replay",
        audience: { custom: { fleetType: ['tourism'] } }
    },
    {
        id: 'high-water-alert',
        label: 'Bilge Pump & High Water Alerts',
        description:
            "Connect a bilge pump float switch and receive alerts when it activates or exceeds a set number of activations in an hour. This feature is currently in beta - please contact us if you're keen to try it out.",
        releasedAt: '2026-09-24',
        showNewUntil: '2026-10-24',
        sidebarKey: "high-water",
    },
    {
        id: 'notification-groups',
        label: 'Notification Groups',
        description:
            "Not everybody needs to receive every notification. Notification Groups can be configured in fleet settings and allow you to control who gets notified for different events. Please send feedback and queries to [fleet.support@nebo.global](mailto:fleet.support@nebo.global).",
        releasedAt: '2026-10-06',
        showNewUntil: '2026-10-16',
        sidebarKey: "fleet-config",
        cta: { label: 'Configure Now', url: '/{fleet}/edit' },
        audience: { custom: { fleetRole: ['administrator'] } }
    }
])
